import React, { useState, useRef, useEffect } from 'react';
import { mapContent } from './Map.content';
import transportLinesSvg from './assets/transport-lines.svg';
import markerEcologySvg from './assets/marker-ecology.svg';
import markerDosugSvg from './assets/mrker-dosug.svg';
import markerLogoSvg from './assets/marker-logo.svg';
import labelEcologySvg from './assets/label-ecology.svg';
import labelTransportSvg from './assets/label-transport.svg';
import labelDosugSvg from './assets/label-dosug.svg';
import styles from './Map.module.sass';

const MARKER_ICONS = {
  ecology: markerEcologySvg,
  dosug: markerDosugSvg,
};

const TAB_ICONS = {
  ecology: labelEcologySvg,
  transport: labelTransportSvg,
  dosug: labelDosugSvg,
};

const CATEGORY_COLORS = {
  ecology: '#25A864',
  dosug: '#FF8F57',
};

function Marker({ marker, activeTab, isVisible, index, viewport }) {
  const isLogo = marker.category === 'logo';
  const isNeutral = marker.category === 'neutral';
  const isActive = marker.category === activeTab;
  const isInactive = !isLogo && !isNeutral && !isActive;
  const labelSide = marker.labelSide || 'right';

  const x =
    viewport === 'mobile' && marker.xMobile !== undefined
      ? marker.xMobile
      : viewport === 'tablet' && marker.xTablet !== undefined
        ? marker.xTablet
        : marker.x;
  const y =
    viewport === 'mobile' && marker.yMobile !== undefined
      ? marker.yMobile
      : viewport === 'tablet' && marker.yTablet !== undefined
        ? marker.yTablet
        : marker.y;

  const markerClass = [styles.map__marker, !isVisible && styles['map__marker--hidden']]
    .filter(Boolean)
    .join(' ');

  const labelClass = [
    styles.map__markerLabel,
    labelSide === 'left' && styles['map__markerLabel--left'],
    labelSide === 'down' && styles['map__markerLabel--down'],
    marker.labelAlwaysVisible && styles['map__markerLabel--alwaysVisible'],
  ]
    .filter(Boolean)
    .join(' ');

  const dotClass = [styles.map__markerDot, isInactive && styles['map__markerDot--inactive']]
    .filter(Boolean)
    .join(' ');

  const categoryColor = CATEGORY_COLORS[marker.category];

  return (
    <div
      className={markerClass}
      style={{
        left: `${(x / 1400) * 100}%`,
        top: `${(y / 872) * 100}%`,
        transitionDelay: isVisible ? `${index * 0.05}s` : '0s',
      }}
    >
      {isLogo ? (
        <img src={markerLogoSvg} alt={marker.title} className={styles.map__markerLogo} />
      ) : isNeutral ? (
        <div className={styles.map__markerNeutral} />
      ) : (
        <div
          className={dotClass}
          style={{
            backgroundColor: isInactive ? '#fff' : categoryColor,
          }}
        >
          {isInactive ? (
            <span
              className={styles.map__markerIconColored}
              style={{
                maskImage: `url(${MARKER_ICONS[marker.category]})`,
                WebkitMaskImage: `url(${MARKER_ICONS[marker.category]})`,
                backgroundColor: categoryColor,
              }}
            />
          ) : (
            <img src={MARKER_ICONS[marker.category]} alt="" className={styles.map__markerIcon} />
          )}
        </div>
      )}
      <span className={labelClass}>{marker.title}</span>
    </div>
  );
}

function InfoBlock({ activeTab, tabs, info, onTabChange, visitedTabs, showPulse }) {
  const activeInfo = info[activeTab];
  const activeTabData = tabs.find((t) => t.id === activeTab);

  return (
    <div className={styles.map__info}>
      <div className={styles.map__infoContent} style={{ backgroundColor: activeTabData.color }}>
        <img src={TAB_ICONS[activeTab]} alt="" className={styles.map__infoIcon} />
        <div className={styles.map__infoTitle}>
          {activeInfo.title.map((line, i) => (
            <span
              key={i}
              className={`${styles.map__infoTitleLine} ${line.accent ? styles['map__infoTitleLine--accent'] : ''}`}
            >
              {line.text}
            </span>
          ))}
        </div>

        {activeInfo.image && (
          <div className={styles.map__infoImageWrap}>
            <img src={activeInfo.image} alt="" className={styles.map__infoImage} decoding="async" />
          </div>
        )}

        <div className={styles.map__infoBody}>
          {activeInfo.body.map((block, i) => (
            <p key={i} className={styles.map__infoText}>
              {block.segments.map((seg, j) => (
                <span
                  key={j}
                  className={
                    seg.accent ? styles['map__infoText--accent'] : styles['map__infoText--muted']
                  }
                >
                  {seg.text.split('\n').map((line, index) => (
                    <span key={index}>
                      {line}
                      {index < seg.text.split('\n').length - 1 && <br />}
                    </span>
                  ))}
                </span>
              ))}
            </p>
          ))}
        </div>
      </div>

      <div className={styles.map__tabs}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const shouldPulse = showPulse && !visitedTabs.has(tab.id) && !isActive;
          const tabClass = [
            styles.map__tab,
            isActive && styles['map__tab--active'],
            shouldPulse && styles['map__tab--pulse'],
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={tab.id}
              className={tabClass}
              style={isActive ? { borderColor: tab.color, color: tab.color } : undefined}
              onClick={() => onTabChange(tab.id)}
            >
              <img
                src={TAB_ICONS[tab.id]}
                alt=""
                className={`${styles.map__tabIcon} ${isActive ? styles['map__tabIcon--active'] : ''}`}
              />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Map() {
  const [activeTab, setActiveTab] = useState('ecology');
  const [isVisible, setIsVisible] = useState(false);
  const [visitedTabs, setVisitedTabs] = useState(new Set(['ecology']));
  const [showPulse, setShowPulse] = useState(false);
  const [transportRenderVersion, setTransportRenderVersion] = useState(0);
  const [viewport, setViewport] = useState('desktop');
  const sectionRef = useRef(null);
  const scrollRef = useRef(null);
  const { tabs, info, markers, title } = mapContent;

  const isMobileView = viewport === 'mobile' || viewport === 'tablet';

  useEffect(() => {
    const updateViewport = () => {
      if (window.matchMedia('(max-width: 500px)').matches) {
        setViewport('mobile');
      } else if (window.matchMedia('(max-width: 900px)').matches) {
        setViewport('tablet');
      } else {
        setViewport('desktop');
      }
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    if (isMobileView && scrollRef.current) {
      const el = scrollRef.current;
      el.scrollLeft = el.scrollWidth - el.clientWidth;
    }
  }, [isMobileView]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setShowPulse(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setVisitedTabs((prev) => new Set([...prev, tabId]));
    if (tabId === 'transport') {
      setTransportRenderVersion((prev) => prev + 1);
    }
  };

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const activeInfo = info[activeTab];
  const activeTabData = tabs.find((t) => t.id === activeTab);

  const renderMarkers = () => (
    <div className={styles.map__markers}>
      {markers.map((marker, index) => (
        <Marker
          key={marker.id}
          marker={marker}
          activeTab={activeTab}
          isVisible={isVisible}
          index={index}
          viewport={viewport}
        />
      ))}
    </div>
  );

  const renderOverlay = () => (
    <div
      className={`${styles.map__overlay} ${activeTab === 'transport' ? styles['map__overlay--dark'] : ''}`}
    />
  );

  const renderTransportLines = () =>
    activeTab === 'transport' && (
      <img
        key={transportRenderVersion}
        src={`${transportLinesSvg}?v=${transportRenderVersion}`}
        alt=""
        className={styles.map__transportLines}
      />
    );

  if (isMobileView) {
    return (
      <section className={styles.map} ref={sectionRef}>
        <div className={styles.map__mobileHeader}>
          <h2 className={styles.map__title}>
            {title.map((line, i) => (
              <span
                key={i}
                className={`${styles.map__titleLine} ${line.accent ? styles['map__titleLine--accent'] : ''}`}
              >
                {line.text}
              </span>
            ))}
          </h2>

          <div className={styles.map__infoContent} style={{ backgroundColor: activeTabData.color }}>
            <div className={styles.map__infoTitle}>
              {activeInfo.title.map((line, i) => (
                <span
                  key={i}
                  className={`${styles.map__infoTitleLine} ${line.accent ? styles['map__infoTitleLine--accent'] : ''}`}
                >
                  {line.text}
                </span>
              ))}
            </div>

            {activeInfo.image && (
              <div className={styles.map__infoImageWrap}>
                <img
                  src={activeInfo.image}
                  alt=""
                  className={styles.map__infoImage}
                  decoding="async"
                />
              </div>
            )}

            <div className={styles.map__infoBody}>
              {activeInfo.body.map((block, i) => (
                <p key={i} className={styles.map__infoText}>
                  {block.segments.map((seg, j) => (
                    <span
                      key={j}
                      className={
                        seg.accent
                          ? styles['map__infoText--accent']
                          : styles['map__infoText--muted']
                      }
                    >
                      {seg.text.split('\n').map((line, index) => (
                        <span key={index}>
                          {line}
                          {index < seg.text.split('\n').length - 1 && <br />}
                        </span>
                      ))}
                    </span>
                  ))}
                </p>
              ))}
            </div>
          </div>

          <div className={styles.map__tabs}>
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const shouldPulse = showPulse && !visitedTabs.has(tab.id) && !isActive;
              const tabClass = [
                styles.map__tab,
                isActive && styles['map__tab--active'],
                shouldPulse && styles['map__tab--pulse'],
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  key={tab.id}
                  className={tabClass}
                  style={isActive ? { borderColor: tab.color, color: tab.color } : undefined}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <img
                    src={TAB_ICONS[tab.id]}
                    alt=""
                    className={`${styles.map__tabIcon} ${isActive ? styles['map__tabIcon--active'] : ''}`}
                  />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.map__mapWrap}>
          <div className={styles.map__scroll} ref={scrollRef}>
            <div className={styles.map__container}>
              {renderOverlay()}
              {renderTransportLines()}
              {renderMarkers()}
            </div>
          </div>
          <div className={styles.map__scrollHint}>
            <span>карту можно передвигать</span>
            <div className={styles.map__scrollArrows}>
              <button className={styles.map__scrollArrowBtn} onClick={handleScrollLeft}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path
                    d="M6 2L3.5 5L6 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button className={styles.map__scrollArrowBtn} onClick={handleScrollRight}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path
                    d="M4 2L6.5 5L4 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.map} ref={sectionRef}>
      <div className={styles.map__container}>
        {renderOverlay()}

        <h2 className={styles.map__title}>
          {title.map((line, i) => (
            <span
              key={i}
              className={`${styles.map__titleLine} ${line.accent ? styles['map__titleLine--accent'] : ''}`}
            >
              {line.text}
            </span>
          ))}
        </h2>

        {renderTransportLines()}
        {renderMarkers()}

        <InfoBlock
          activeTab={activeTab}
          tabs={tabs}
          info={info}
          onTabChange={handleTabChange}
          visitedTabs={visitedTabs}
          showPulse={showPulse}
        />
      </div>
    </section>
  );
}
